import { FunctionComponent, useMemo } from "react";
import AutoSizer from "react-virtualized-auto-sizer";

import { Button } from "antd";
import styled, { keyframes } from "styled-components/macro";

import { useUser } from "hooks";

import { ProductionVolumesResponse } from "api/useProductionVolumesQuery";

import { StyledGrid, paginationHeight, rdgThemeClassname } from "./DataTable.styles";
import Pagination from "./Pagination";
import { useColumnCopy } from "./columnCopy";
import useMonthlyProduction from "./hooks/useMonthlyProduction";
import {
  CORE_COLUMNS,
  MonthlyCopyContext,
  NO_ROWS,
  Row,
  SKELETON_ROWS,
  rowClass,
  toGridColumns,
  toRowMeta
} from "./monthlyColumns";
import {
  GridPanel as PanelKind,
  getGridState,
  noProductionText
} from "./monthlyGridState";

// react-data-grid's own header row height, so the state panels start under the header.
const HEADER_ROW_HEIGHT = 35;

const StatePanel: FunctionComponent<{
  panel?: PanelKind;
  data?: ProductionVolumesResponse;
  onRetry: () => void;
}> = ({ panel, data, onRetry }) => {
  if (panel === "skeleton") {
    return (
      <span className="visually-hidden" role="status">
        Loading monthly production
      </span>
    );
  }
  if (panel === "emptyFilter") {
    return (
      <Panel role="status" aria-live="polite">
        <PanelTitle>No wells in the current filter</PanelTitle>
        <PanelText>Adjust the filters in the Focus panel or the map view.</PanelText>
      </Panel>
    );
  }
  if (panel === "noProduction" && data) {
    return (
      <Panel role="status" aria-live="polite">
        <PanelTitle>No monthly production</PanelTitle>
        <PanelText>{noProductionText(data)}</PanelText>
      </Panel>
    );
  }
  if (panel === "error") {
    return (
      <Panel role="alert">
        <PanelTitle>Couldn&apos;t load monthly production</PanelTitle>
        <PanelText>The request failed. Your filter is unchanged.</PanelText>
        <Button type="primary" onClick={onRetry}>
          Retry
        </Button>
      </Panel>
    );
  }
  return null;
};

interface MonthlyProductionGridProps {
  // Wells hidden on the map hide their rows here too, as in the Data grid.
  rowsHidden: boolean;
  // The Data view's header color; the grid's default header applies until it is known.
  headerColor?: string;
}

const MonthlyProductionGrid: FunctionComponent<MonthlyProductionGridProps> = ({
  rowsHidden,
  headerColor
}) => {
  const {
    size,
    maxPageSize,
    pagination,
    totalWells,
    isEmptyFilter,
    data,
    isLoading,
    isFetching,
    isPreviousData,
    isError,
    refetch
  } = useMonthlyProduction();

  const { hasTrialAccount, isReadonly } = useUser();
  const { copied, copyColumnContents, copyCellValue } = useColumnCopy();
  const canCopy = !hasTrialAccount && !isReadonly;

  const rows = data?.rows ?? NO_ROWS;
  const rowMeta = useMemo(() => toRowMeta(rows), [rows]);

  const columns = useMemo(
    () => toGridColumns(data?.columns ?? CORE_COLUMNS, rowMeta, headerColor),
    [data?.columns, rowMeta, headerColor]
  );

  const { gridRows, panel } = getGridState({
    rowsHidden,
    isEmptyFilter,
    isLoading,
    isError,
    data
  });
  const refreshing = isPreviousData && isFetching && !!data;

  const copyContext = useMemo(
    () => ({
      enabled: canCopy,
      copied,
      onCopyColumn: (name: string, key: string) => copyColumnContents(name, rows, key)
    }),
    [canCopy, copied, copyColumnContents, rows]
  );

  // Raw values at full precision, as the Copy button does; a null cell copies as empty.
  const handleCellClick = (_: number, row: Row, column: { key: string }) => {
    if (!canCopy || SKELETON_ROWS.includes(row)) return;
    copyCellValue(row[column.key] ?? "");
  };

  return (
    <Body>
      <GridArea
        aria-busy={panel === "skeleton" || refreshing}
        data-refreshing={refreshing}>
        {refreshing && <ProgressBar data-testid="monthly-progress" />}
        <AutoSizer>
          {({ width, height }) => (
            <MonthlyCopyContext.Provider value={copyContext}>
              <MonthlyGrid
                className={rdgThemeClassname}
                columns={columns}
                rows={gridRows}
                rowClass={rowClass(rowMeta)}
                onRowClick={handleCellClick}
                style={{ width: `${Number(width)}px`, height: `${Number(height)}px` }}
              />
            </MonthlyCopyContext.Provider>
          )}
        </AutoSizer>
        <StatePanel panel={panel} data={data} onRetry={() => refetch()} />
      </GridArea>
      {pagination && (
        <Footer>
          <Pagination
            pageSize={size}
            maxPageSize={maxPageSize}
            totalResults={totalWells ?? 0}
          />
        </Footer>
      )}
    </Body>
  );
};

export default MonthlyProductionGrid;

const progress = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(250%); }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const GridArea = styled.div`
  position: relative;
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;

  &[data-refreshing="true"] .rdg {
    opacity: 0.5;
  }
`;

const Footer = styled.div`
  flex: 0 0 ${paginationHeight}px;
`;

const ProgressBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  width: 40%;
  height: 2px;
  background-color: var(--color-primary);
  animation: ${progress} 1.2s var(--ease) infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    width: 100%;
  }
`;

const MonthlyGrid = styled(StyledGrid)`
  // Body cells only: the header row keeps the Data grid's own header styling.
  .rdg-row:not(.rdg-header-row) .rdg-cell {
    font-family: var(--fontMono);
    font-variant-numeric: tabular-nums;
  }

  && .monthly-alternate-well {
    background-color: var(--header-background-color);
  }

  && .rdg-row:hover {
    background-color: var(--row-hover-background-color);
  }

  && .monthly-first-row .rdg-cell {
    border-top: 2px solid rgba(var(--color-text-rgb), 0.35);
  }

  .monthly-number {
    text-align: right;
  }

  .monthly-muted {
    color: var(--color-action-button);
  }
`;

const Panel = styled.div`
  position: absolute;
  top: ${HEADER_ROW_HEIGHT}px;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  background-color: var(--color-background);
`;

const PanelTitle = styled.div`
  font-size: 1.6rem;
  font-weight: var(--fontWeightBold);
`;

const PanelText = styled.div`
  color: var(--color-text-60);
`;
